// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — hold a real-time overlay's grip and drag its row up or down to
 * change the order the overlays are drawn in (owner, 2026-10-09: "click,
 * hold and drag, limited to up and down", instead of arrows that made the
 * rows thicker or too small for a thumb).
 *
 * Used by the browser's date tracks and by the phone's AR controls, so a
 * row moves the same way under a mouse and a finger:
 *
 *   - a mouse lifts the row as soon as it presses; a finger or a pen
 *     holds for a moment first (`HOLD_MS`), so a quick swipe across the
 *     panel never reorders anything;
 *   - the row follows the pointer up and down only, and stops at the top
 *     row and at the bottom row (the dataset's track is not one of the
 *     rows, so nothing passes it); the others slide aside to show where
 *     it will land;
 *   - letting go drops it there (`onDrop`), Escape puts it back;
 *   - on the keyboard the grip is a button: ↑ and ↓ move the row one place.
 *
 * Rows are top first here; callers turn that into their own order.
 */

import { dropIndex } from '../services/overlayDisplay'

/** How long a finger holds the grip before the row lifts. */
export const HOLD_MS = 200
/** A finger that moves this far during the hold was not holding. */
const HOLD_SLOP_PX = 8

export interface StackDragOptions {
  /** The rows that can move, top first, the dragged one among them. */
  rows(): HTMLElement[]
  /** The row this grip belongs to. */
  row(): HTMLElement
  /** The row was let go at `to` (top-first index); it started at `from`. */
  onDrop(from: number, to: number): void
  /** While dragging, where the row would land, each time that changes. */
  onPreview?(from: number, to: number): void
}

interface Drag {
  row: HTMLElement
  rows: HTMLElement[]
  from: number
  to: number
  startY: number
  step: number
  minDy: number
  maxDy: number
  pointerId: number
}

/** Wire one grip. Returns a function that unwires it. */
export function attachStackDrag(grip: HTMLElement, opts: StackDragOptions): () => void {
  let drag: Drag | null = null
  let hold: { timer: ReturnType<typeof setTimeout>; x: number; y: number; pointerId: number } | null = null

  function begin(y: number, pointerId: number): void {
    hold = null
    const row = opts.row()
    const rows = opts.rows()
    const from = rows.indexOf(row)
    if (from < 0 || rows.length < 2) {
      row.classList.remove('stack-lifting')
      return
    }
    const tops = rows.map((r) => r.getBoundingClientRect().top)
    drag = {
      row, rows, from, to: from, startY: y, pointerId,
      step: (tops[tops.length - 1]! - tops[0]!) / (rows.length - 1),
      minDy: tops[0]! - tops[from]!,
      maxDy: tops[tops.length - 1]! - tops[from]!,
    }
    row.classList.remove('stack-lifting')
    row.classList.add('stack-dragging')
    for (const r of rows) if (r !== row) r.classList.add('stack-shifting')
    document.body.classList.add('stack-drag-active')
  }

  function move(y: number): void {
    const d = drag
    if (!d) return
    const dy = Math.max(d.minDy, Math.min(d.maxDy, y - d.startY))
    d.row.style.transform = `translateY(${dy}px)`
    const to = dropIndex(d.from, dy, d.step, d.rows.length)
    if (to === d.to) return
    d.to = to
    d.rows.forEach((r, i) => {
      if (r === d.row) return
      const shift = d.from < i && i <= to ? -d.step : to <= i && i < d.from ? d.step : 0
      r.style.transform = shift ? `translateY(${shift}px)` : ''
    })
    opts.onPreview?.(d.from, to)
  }

  function end(commit: boolean): void {
    const d = drag
    drag = null
    document.body.classList.remove('stack-drag-active')
    if (!d) return
    for (const r of d.rows) {
      r.classList.remove('stack-dragging', 'stack-shifting')
      r.style.transform = ''
    }
    if (commit && d.to !== d.from) opts.onDrop(d.from, d.to)
    else if (d.to !== d.from) opts.onPreview?.(d.from, d.from)
  }

  function cancelHold(): void {
    if (!hold) return
    clearTimeout(hold.timer)
    hold = null
    opts.row().classList.remove('stack-lifting')
  }

  const onPointerDown = (ev: PointerEvent): void => {
    if (ev.button !== 0 || (grip as HTMLButtonElement).disabled) return
    ev.preventDefault()
    try {
      grip.setPointerCapture(ev.pointerId)
    } catch {
      // Capture keeps the drag when the pointer leaves the grip; it still works without.
    }
    if (ev.pointerType === 'mouse') {
      begin(ev.clientY, ev.pointerId)
      return
    }
    opts.row().classList.add('stack-lifting')
    const startY = ev.clientY
    hold = { x: ev.clientX, y: startY, pointerId: ev.pointerId, timer: setTimeout(() => begin(startY, ev.pointerId), HOLD_MS) }
  }

  const onPointerMove = (ev: PointerEvent): void => {
    if (hold) {
      if (Math.hypot(ev.clientX - hold.x, ev.clientY - hold.y) > HOLD_SLOP_PX) cancelHold()
      return
    }
    if (drag && ev.pointerId === drag.pointerId) move(ev.clientY)
  }

  const onPointerUp = (ev: PointerEvent): void => {
    if (hold) {
      cancelHold()
      return
    }
    if (drag && ev.pointerId === drag.pointerId) end(ev.type === 'pointerup')
  }

  const onKeyDown = (ev: KeyboardEvent): void => {
    if (drag && ev.key === 'Escape') {
      ev.preventDefault()
      ev.stopPropagation()
      end(false)
      return
    }
    if (ev.key !== 'ArrowUp' && ev.key !== 'ArrowDown') return
    const rows = opts.rows()
    const from = rows.indexOf(opts.row())
    if (from < 0) return
    ev.preventDefault()
    const to = from + (ev.key === 'ArrowUp' ? -1 : 1)
    if (to >= 0 && to < rows.length) opts.onDrop(from, to)
  }

  // Escape anywhere while a row is in the air puts it back.
  const onDocKeyDown = (ev: KeyboardEvent): void => {
    if (drag && ev.key === 'Escape') end(false)
  }

  grip.addEventListener('pointerdown', onPointerDown)
  grip.addEventListener('pointermove', onPointerMove)
  grip.addEventListener('pointerup', onPointerUp)
  grip.addEventListener('pointercancel', onPointerUp)
  grip.addEventListener('lostpointercapture', onPointerUp)
  grip.addEventListener('keydown', onKeyDown)
  document.addEventListener('keydown', onDocKeyDown)
  return () => {
    cancelHold()
    end(false)
    grip.removeEventListener('pointerdown', onPointerDown)
    grip.removeEventListener('pointermove', onPointerMove)
    grip.removeEventListener('pointerup', onPointerUp)
    grip.removeEventListener('pointercancel', onPointerUp)
    grip.removeEventListener('lostpointercapture', onPointerUp)
    grip.removeEventListener('keydown', onKeyDown)
    document.removeEventListener('keydown', onDocKeyDown)
  }
}
